import type { KeyValuePair } from "../../../../utils/sys/KeyValuePair";

import {ReactElement, ReactNode, useCallback, useMemo, useState} from "react";

import Button from "../../../../common/components/input/Button/Button";
import CollapsibleSection from "../../../../common/components/display/CollapsibleSection/CollapsibleSection";
import DiceRollDisplay from "../../../../common/components/display/DiceRollDisplay/DiceRollDisplay";
import Drawer from "../../../../common/components/layout/Drawer/Drawer";
import Flexbox from "../../../../common/components/layout/Flexbox/Flexbox";
import NumericSpinner from "../../../../common/components/input/NumericSpinner/NumericSpinner";

import "./DiceDrawer_style.css";

type Props = Readonly<{
    alignment?: "top" | "right" | "bottom" | "left",
    isVisible: boolean,
    width?: number | null,
    height?: number | null,
    onClose?: () => void,
    showCloseButton?: boolean,
    diceCounts: ReadonlyArray<KeyValuePair<number, number>>,
    onDiceCountsChange?: (value: Array<KeyValuePair<number, number>>) => void,
}>;

export default function DiceDrawer({alignment = "left", isVisible, width = 300, height, onClose, showCloseButton, diceCounts, onDiceCountsChange}: Props): ReactElement {
    const [d2Count, setD2Count] = useState<number>(diceCounts.find(d => d.key === 2)?.value ?? 0);
    const [d4Count, setD4Count] = useState<number>(diceCounts.find(d => d.key === 4)?.value ?? 0);
    const [d6Count, setD6Count] = useState<number>(diceCounts.find(d => d.key === 6)?.value ?? 0);
    const [d8Count, setD8Count] = useState<number>(diceCounts.find(d => d.key === 8)?.value ?? 0);
    const [d10Count, setD10Count] = useState<number>(diceCounts.find(d => d.key === 10)?.value ?? 0);
    const [d12Count, setD12Count] = useState<number>(diceCounts.find(d => d.key === 12)?.value ?? 0);
    const [d20Count, setD20Count] = useState<number>(diceCounts.find(d => d.key === 20)?.value ?? 0);
    const [d100Count, setD100Count] = useState<number>(diceCounts.find(d => d.key === 100)?.value ?? 0);

    const [d2RollTimestamp, setD2RollTimestamp] = useState<number | null>(null);
    const [d4RollTimestamp, setD4RollTimestamp] = useState<number | null>(null);
    const [d6RollTimestamp, setD6RollTimestamp] = useState<number | null>(null);
    const [d8RollTimestamp, setD8RollTimestamp] = useState<number | null>(null);
    const [d10RollTimestamp, setD10RollTimestamp] = useState<number | null>(null);
    const [d12RollTimestamp, setD12RollTimestamp] = useState<number | null>(null);
    const [d20RollTimestamp, setD20RollTimestamp] = useState<number | null>(null);
    const [d100RollTimestamp, setD100RollTimestamp] = useState<number | null>(null);

    const [isDiceSelectionExpanded, setIsDiceSelectionExpanded] = useState<boolean>(true);
    const [isDiceBagExpanded, setIsDiceBagExpanded] = useState<boolean>(true);

    const dice = useMemo((): ReadonlyArray<KeyValuePair<number, Array<ReactNode>>> => {
        return diceCounts.map((entry): KeyValuePair => {
            const d: Array<ReactElement> = [];
            const timestamp = 
                entry.key === 2 ? d2RollTimestamp : 
                entry.key === 4 ? d4RollTimestamp : 
                entry.key === 6 ? d6RollTimestamp : 
                entry.key === 8 ? d8RollTimestamp :
                entry.key === 10 ? d10RollTimestamp :
                entry.key === 12 ? d12RollTimestamp :
                entry.key === 20 ? d20RollTimestamp :
                d100RollTimestamp

            for(let i = 0; i < entry.value; ++i) {
                d.push(<DiceRollDisplay diceSides={entry.key} externalRollTriggerTimestamp={timestamp} />);
            }

            return {key: entry.key, value: d};
        });
    }, [d100RollTimestamp, d10RollTimestamp, d12RollTimestamp, d20RollTimestamp, d2RollTimestamp, d4RollTimestamp, d6RollTimestamp, d8RollTimestamp, diceCounts]);

    const updateDiceCount = useCallback((sides: number, newCount: number) => {
        if(newCount === 0) {
            if(diceCounts.find(entry => entry.key === sides)) {
                onDiceCountsChange?.(diceCounts.filter(d => d.key !== sides));
            }
        } else {
            if(diceCounts.find(entry => entry.key === sides)) {
                onDiceCountsChange?.(diceCounts.map(d => d.key === sides ? {key: sides, value: newCount} : d));
            } else {
                onDiceCountsChange?.([...diceCounts, {key: sides, value: newCount}]);
            }
        }
    }, [diceCounts, onDiceCountsChange]);

    return <Drawer alignment={alignment} isVisible={isVisible} width={width} height={height} onClose={onClose} showCloseButton={showCloseButton}>
        <Flexbox direction="column" gap={12}>
            <CollapsibleSection headerText="Dice Selection" isExpanded={isDiceSelectionExpanded} onIsExpandedChange={setIsDiceSelectionExpanded}>
                <Flexbox direction="row" gap={8}>
                    <NumericSpinner 
                        label="D2"
                        value={d2Count}
                        max={20}
                        onChange={(val) => {
                            setD2Count(() => {
                                updateDiceCount(2, val);

                                return val;
                            })
                        }}
                    />
                    <NumericSpinner
                        label="D4"
                        value={d4Count}
                        max={20}
                        onChange={(val) => {
                            setD4Count(() => {
                                updateDiceCount(4, val);

                                return val;
                            })
                        }} 
                    />
                </Flexbox>
                <Flexbox direction="row" gap={8}>
                    <NumericSpinner 
                        label="D6"
                        value={d6Count}
                        max={20}
                        onChange={(val) => {
                            setD6Count(() => {
                                updateDiceCount(6, val);

                                return val;
                            })
                        }} 
                    />
                    <NumericSpinner 
                        label="D8"
                        value={d8Count}
                        max={20}
                        onChange={(val) => {
                            setD8Count(() => {
                                updateDiceCount(8, val);

                                return val;
                            })
                        }} 
                    />
                </Flexbox>
                <Flexbox direction="row" gap={8}>
                    <NumericSpinner 
                        label="D10"
                        value={d10Count}
                        max={20}
                        onChange={(val) => {
                            setD10Count(() => {
                                updateDiceCount(10, val);

                                return val;
                            })
                        }} 
                    />
                    <NumericSpinner 
                        label="D12"
                        value={d12Count}
                        max={20}
                        onChange={(val) => {
                            setD12Count(() => {
                                updateDiceCount(12, val);

                                return val;
                            })
                        }} 
                    />
                </Flexbox>
                <Flexbox direction="row" gap={8}>
                    <NumericSpinner 
                        label="D20"
                        value={d20Count}
                        max={20}
                        onChange={(val) => {
                            setD20Count(() => {
                                updateDiceCount(20, val);

                                return val;
                            })
                        }} 
                    />
                    <NumericSpinner 
                        label="D100"
                        value={d100Count}
                        max={20}
                        onChange={(val) => {
                            setD100Count(() => {
                                updateDiceCount(100, val);

                                return val;
                            })
                        }} 
                    />
                </Flexbox>
                <Button label="Reset" style={{width: "100%", marginTop: "16px"}} onClick={() => {
                    setD2Count(0);
                    setD4Count(0);
                    setD6Count(0);
                    setD8Count(0);
                    setD10Count(0);
                    setD12Count(0);
                    setD20Count(0);
                    setD100Count(0);
                    onDiceCountsChange?.([]);
                }} />
            </CollapsibleSection>
            <hr />
            <CollapsibleSection headerText="Dice Bag" isExpanded={isDiceBagExpanded} onIsExpandedChange={setIsDiceBagExpanded} isDisabled={dice.length === 0}>
                <Flexbox direction="column" gap={16}>
                    {dice.flatMap(row =>
                        <Flexbox direction="column" gap={8}>
                            <Flexbox direction="row" wrap={true}>
                                {row.value}
                            </Flexbox>
                            <Button label={`Roll D${row.key}s`} onClick={() => {
                                switch(row.key) {
                                    case 2:
                                        setD2RollTimestamp(Date.now());
                                        setTimeout(() => {
                                            setD2RollTimestamp(null);
                                        }, 500);
                                        break;
                                    case 4:
                                        setD4RollTimestamp(Date.now());
                                        setTimeout(() => {
                                            setD4RollTimestamp(null);
                                        }, 500);
                                        break;
                                    case 6:
                                        setD6RollTimestamp(Date.now());
                                        setTimeout(() => {
                                            setD6RollTimestamp(null);
                                        }, 500);
                                        break;
                                    case 8:
                                        setD8RollTimestamp(Date.now());
                                        setTimeout(() => {
                                            setD8RollTimestamp(null);
                                        }, 500);
                                        break;
                                    case 10:
                                        setD10RollTimestamp(Date.now());
                                        setTimeout(() => {
                                            setD10RollTimestamp(null);
                                        }, 500);
                                        break;
                                    case 12:
                                        setD12RollTimestamp(Date.now());
                                        setTimeout(() => {
                                            setD12RollTimestamp(null);
                                        }, 500);
                                        break;
                                    case 20:
                                        setD20RollTimestamp(Date.now());
                                        setTimeout(() => {
                                            setD20RollTimestamp(null);
                                        }, 500);
                                        break;
                                    case 100:
                                        setD100RollTimestamp(Date.now());
                                        setTimeout(() => {
                                            setD100RollTimestamp(null);
                                        }, 500);
                                        break;
                                }
                                
                            }} />
                        </Flexbox>)}
                    {dice.length > 0 && <Button label="Roll All" onClick={() => {
                            const timestamp = Date.now();

                            setD2RollTimestamp(timestamp);
                            setD4RollTimestamp(timestamp);
                            setD6RollTimestamp(timestamp);
                            setD8RollTimestamp(timestamp);
                            setD10RollTimestamp(timestamp);
                            setD12RollTimestamp(timestamp);
                            setD20RollTimestamp(timestamp);
                            setD100RollTimestamp(timestamp);

                            setTimeout(() => {
                                setD2RollTimestamp(null);
                                setD4RollTimestamp(null);
                                setD6RollTimestamp(null);
                                setD8RollTimestamp(null);
                                setD10RollTimestamp(null);
                                setD12RollTimestamp(null);
                                setD20RollTimestamp(null);
                                setD100RollTimestamp(null);
                            }, 500);
                        }} />
                    }
                </Flexbox>
            </CollapsibleSection>
        </Flexbox>
    </Drawer>;
}